/**
 * 平台 Admin API 边界（全站真实后端，禁止 mock）
 * 正式前缀：/admin/api/v1
 *
 * FE-E2b 薄壳化：公共核心（401 单飞刷新/幂等键/信封解析/超时/追踪码）下沉
 * @shared/api/createClient 工厂；本文件只保留 Admin 端配置、类型与端特有分支
 * （X-Operator-Id 身份头 / Authorization 过滤 / blob 下载），导出面与行为不变
 * （FE-E2a 契约测试 client.contract.test.ts 锁定）。
 *
 * API base 直写代码（dev 走 Vite proxy 相对路径；生产构建固定 Tunnel 绝对路径），
 * 不再使用 .env / VITE_* 注入。联调前置：确保 Vite proxy `/admin/api` → localhost:8081。
 */
import { createClient } from '@shared/api/createClient';

// ================= 会话与领域视图类型（对齐后端 DTO） =================

/** 认证渠道（对齐后端 AuthChannel 枚举字面量） */
export type AuthChannel = 'ADMIN' | 'AGENT' | 'MERCHANT' | 'CARDHOLDER';

/**
 * 当前会话身份视图（对齐后端 PortalSessionView record）。
 * Admin 门户无作用域 id（不消费 merchantId/agentId/cardholderId）。
 */
export type PortalSessionView = {
  subjectId?: number;
  channel?: AuthChannel;
  principal?: string;
  roles?: string[];
  permissions?: string[];
  mustChangePassword?: boolean;
  mfaSetupRequired?: boolean;
  /** OTP 宽限剩余秒；无宽限为 0（对齐后端 PortalSessionView） */
  otpGraceRemainingSeconds?: number;
};

/** 运行时菜单节点（对齐后端 MenuNodeView record） */
export type MenuNodeView = {
  id: string;
  title: string;
  path?: string | null;
  permission?: string | null;
  children?: MenuNodeView[];
};

/** Admin 会话（无作用域 id；登录后写入 me/menus） */
export type AdminSession = {
  accessToken?: string;
  refreshToken?: string;
  principal?: string;
  /** /auth/me 结果（登录后写入；拉取失败置 null） */
  me?: PortalSessionView | null;
  /** /auth/menus 结果（登录后写入；拉取失败置 null） */
  menus?: MenuNodeView[] | null;
  /** accessToken 预估过期时间戳（ms）；缺省表示未知 */
  expiresAt?: number;
};

/**
 * admin 门户统一 API 结果：
 * - ok=true：data 为业务数据
 * - ok=false：error 为后端 message（或 HTTP 提示）；code 为后端业务错误码
 *   （如 OTP 专项 3410/3411/3412/3415，供 OtpConfirmDialog 分流展示）；
 *   traceId 为后端信封追踪码（系统级错误时 error 已附加，字段供页面自行展示）
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; code?: string; error: string; traceId?: string };

/**
 * 全站强制真实后端（运营平台已移除 mock）。
 * 恒为 false：mock 回调保留但永不执行，所有请求走真实 HTTP。
 */
export const USE_MOCK = false;

/** 会话写入后广播，供 RequireSetup / Layout 重读 me 标志 */
export const SESSION_CHANGED_EVENT = 'allinks-session';

// ================= 工厂实例（端差异参数化） =================

const core = createClient<AdminSession>({
  apiBase: {
    dev: '/admin/api/v1',
    prod: 'https://allinks-api.swapsle.com/admin/api/v1',
  },
  sessionKey: 'allinks_admin_session',
  loginPath: '/login',
  // Admin 特有：部分写接口（开户审核 claim/review 等）仍读取 X-Operator-Id；
  // 优先用 /auth/me 的 subjectId，与 JWT 身份一致，避免漏带头导致「X-Operator-Id 不能为空」。
  extraHeaders: (s) => {
    const h: Record<string, string> = {};
    const subjectId = s?.me?.subjectId;
    if (subjectId != null && Number.isFinite(Number(subjectId)) && Number(subjectId) > 0) {
      h['X-Operator-Id'] = String(subjectId);
    }
    return h;
  },
  // 少量显式业务确认头；不得由调用方伪造 Authorization（admin 端过滤策略）。
  customHeaderMode: 'filter-auth',
  // Admin 幂等键回退形态：idem-{ts}（无随机后缀，保持原行为）
  jsonKeyGen: 'ts',
  uploadKeyGen: 'ts',
  // FeeItem 软删等 DELETE 写接口要求 Idempotency-Key
  deleteIdempotencyKey: true,
});

export const ADMIN_API_BASE = core.apiBase;

export function adminApiUrl(path: string): string {
  return core.apiUrl(path);
}

export function loadSession(): AdminSession | null {
  return core.loadSession();
}

export function saveSession(s: AdminSession) {
  core.saveSession(s);
}

export function clearSession() {
  core.clearSession();
}

export function setAuthFailureHandler(fn: (() => void) | null) {
  core.setAuthFailureHandler(fn);
}

/** 清会话 + 跳登录：refresh 失败 / 非过期 401 / 主动登出均走此路径 */
export function signOutLocal() {
  core.signOutLocal();
}

export type FileDownload = { blob: Blob; fileName: string };

// ================= 公共 API（保持既有签名，向后兼容） =================

export async function adminGet<T>(
  path: string,
  mock: () => T,
  opts?: { headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('GET', path, undefined, opts);
}

export async function adminPost<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('POST', path, body, opts);
}

/** Admin 写操作（更新）；走 fetchWithRefresh 以获得 401 refresh 重放 */
export async function adminPut<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('PUT', path, body, opts);
}

/** Admin 部分更新；用于显式状态机 PATCH。 */
export async function adminPatch<T>(
  path: string,
  body: unknown,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('PATCH', path, body, opts);
}

/** Admin 写操作（删除/软删）；自动注入 Idempotency-Key；走 401 refresh 重放 */
export async function adminDelete<T>(
  path: string,
  mock: () => T,
  opts?: { skipAuth?: boolean; headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.fetchWithRefresh<T>('DELETE', path, undefined, opts);
}

/** multipart 文件上传；与 JSON client 复用会话和 401 refresh 契约；opts.headers 透传（如 X-OTP）。 */
export async function adminUpload<T>(
  path: string,
  formData: FormData,
  mock: () => T,
  opts?: { headers?: Record<string, string> },
): Promise<ApiResult<T>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  return core.upload<T>(path, formData, { headers: opts?.headers });
}

/** 鉴权文件下载；401 refresh 后只重放一次 GET。 */
export async function adminDownload(
  path: string,
  mock: () => FileDownload,
): Promise<ApiResult<FileDownload>> {
  if (USE_MOCK) {
    await Promise.resolve();
    return { ok: true, data: mock() };
  }
  const { buildHeaders, timeoutSignal, appendTraceHint, retryOnceOn401, TRANSFER_TIMEOUT_MS } =
    core.internals;
  // 本地适配工厂 RawResult 形状：ok:true 携带 { blob, fileName }，供 401 重放器泛型闭合
  const rawDownload = async (): Promise<
    { ok: true; data: FileDownload } | { ok: false; status: number; code?: string; error: string; traceId?: string }
  > => {
    try {
      const res = await fetch(adminApiUrl(path), {
        method: 'GET',
        headers: buildHeaders(false),
        credentials: 'include',
        signal: timeoutSignal(TRANSFER_TIMEOUT_MS),
      });
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        let code: string | undefined;
        let traceId: string | undefined;
        try {
          const body = await res.json() as { message?: string; code?: string | number; traceId?: string };
          message = body.message || message;
          code = body.code === undefined ? undefined : String(body.code);
          traceId = body.traceId;
        } catch { /* 文件接口错误体不一定是 JSON */ }
        return {
          ok: false,
          status: res.status,
          code,
          error: appendTraceHint(code, message, traceId),
          traceId,
        };
      }
      const disposition = res.headers.get('Content-Disposition') || '';
      const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const fileName = encoded ? decodeURIComponent(encoded) : plain || 'download';
      return { ok: true, data: { blob: await res.blob(), fileName } };
    } catch (e) {
      return { ok: false, status: 0, error: e instanceof Error ? e.message : 'network_error' };
    }
  };
  const res = await retryOnceOn401(rawDownload);
  return res.ok
    ? { ok: true, data: res.data }
    : { ok: false, code: res.code, error: res.error, traceId: res.traceId };
}
